"use client"

import { useState, useEffect } from "react"
import { loadStripe } from "@stripe/stripe-js"
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js"

import { getPaymentMethods } from "../utils/getPaymentMethods"
import { CreditCard, Star, Trash2 } from "lucide-react"
import { useStore } from "../store";



async function createPaymentMethod() {

  try {
    const response = await fetch(process.env.REACT_APP_API_HOST + "billing/payment-methods/add", {
      method: "POST",
      headers: {
        accept: "application/json",
        Authorization: "Bearer " + localStorage.getItem("tokenLida"),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ type: "card" }),
    })

    if (!response.ok) {
      throw new Error("Failed to create payment method")
    }

    const data = await response.json()
    return data.clientSecret
  } catch (error) {
    console.error("Error creating payment method:", error)
    throw error
  }
}

async function makeDefaultPaymentMethod(paymentMethodId: string) {

  try {
    const response = await fetch(process.env.REACT_APP_API_HOST + "billing/payment-methods/make-default", {
      method: "POST",
      headers: {
        accept: "application/json",
        Authorization: "Bearer " + localStorage.getItem("tokenLida"),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ payment_method_id: paymentMethodId }),
    })

    if (!response.ok) {
      throw new Error("Failed to set default payment method")
    }

    return await response.json()
  } catch (error) {
    console.error("Error setting default payment method:", error)
    throw error
  }
}

async function deletePaymentMethod(paymentMethodId: string) {

  try {
    const response = await fetch(process.env.REACT_APP_API_HOST + `billing/payment-methods/${paymentMethodId}`, {
      method: "DELETE",
      headers: {
        accept: "application/json",
        Authorization: "Bearer " + localStorage.getItem("tokenLida"),
      },
    })

    if (!response.ok) {
      throw new Error("Failed to delete payment method")
    }

    const data = await response.json()
    return data
  } catch (error) {
    console.error("Error deleting payment method:", error)
    throw error
  }
}

function AddCardForm({ onCardAdded }: { onCardAdded: () => void }) {

  const stripe = useStripe()
  const elements = useElements()
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!stripe || !elements) {
      return
    }

    setLoading(true)
    setError(null)

    try {
      const clientSecret = await createPaymentMethod()
      const cardElement = elements.getElement(CardElement)

      if (cardElement) {
        const { error, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
          payment_method: { card: cardElement },
        })

        if (error) {
          setError(error.message || "An error occurred while processing your card.")
        } else {
          onCardAdded() // Call the onCardAdded function to refresh payment methods
        }
      }
    } catch (err) {
      setError("An error occurred while creating the payment method.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="mt-3">
      <div className="mb-3">
        <label htmlFor="card-element" className="form-label">
          Credit or debit card
        </label>
        <div className="form-control p-3">
          <CardElement id="card-element" />
        </div>
      </div>
      {error && <div className="alert alert-danger">{error}</div>}
      <button type="submit" className="btn btn-primary" disabled={loading}>
        {loading ? "Processing..." : "Add Card"}
      </button>
    </form>
  )
}

interface PaymentMethod {
  id: string
  card: {
    brand: string
    exp_month: number
    exp_year: number
    last4: string
  }
  billing_details: {
    name: string | null
  }
  default?: boolean
}

export default function PaymentMethodsSection() {
    const { auth, setAuth } = useStore();
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY as string)
  const [isAddingCard, setIsAddingCard] = useState(false)
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchPaymentMethods = async () => {
    setLoading(true)
    try {
      const data = await getPaymentMethods()
      setPaymentMethods(data.payment_methods)
      setError(null)
    } catch (err) {
      setError("Failed to fetch payment methods")
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchPaymentMethods()
  }, []) //Fixed: Added empty dependency array to useEffect

  const handleMakeDefault = async (paymentMethodId: string) => {
    try {
      await makeDefaultPaymentMethod(paymentMethodId)
      setPaymentMethods((prevMethods) =>
        prevMethods.map((method) => ({
          ...method,
          default: method.id === paymentMethodId,
        })),
      )
    } catch (err) {
      console.error("Error making payment method default:", err)
      setError("Failed to set default payment method")
    }
  }

  const handleDeletePaymentMethod = async (paymentMethodId: string) => {
    try {
      await deletePaymentMethod(paymentMethodId)
      setPaymentMethods((prevMethods) => prevMethods.filter((method) => method.id !== paymentMethodId))
    } catch (err) {
      console.error("Error deleting payment method:", err)
      setError("Failed to delete payment method")
    }
  }

  const handleCardAdded = () => {
    setIsAddingCard(false)
    fetchPaymentMethods()
  }

  if (loading) {
    return <div>Loading payment methods...</div>
  }

  if (error) {
    return <div className="text-danger">{error}</div>
  }

  return (
    <div>
      <h2 className="mb-4">Payment Methods</h2>
      {paymentMethods.length === 0 ? (
        <div className="alert alert-info">No payment methods added. Please add a new payment method.</div>
      ) : (
        <div className="row row-cols-1 row-cols-md-2 g-4 mb-4">
          {paymentMethods.map((method) => (
            <div key={method.id} className="col">
              <div className={`card h-100 ${method.default ? "border-primary" : ""}`}>
                <div className="card-body">
                  <h5 className="card-title d-flex justify-content-between align-items-center">
                    <span>
                      <CreditCard className="me-2" />
                      {method.card.brand.charAt(0).toUpperCase() + method.card.brand.slice(1)}
                    </span>
                    <div>
                      <button
                        className="btn btn-link p-0 me-2"
                        onClick={() => handleMakeDefault(method.id)}
                        title={method.default ? "Default payment method" : "Make default"}
                      >
                        {method.default ? (
                          <Star className="text-warning" size={20} />
                        ) : (
                          <Star className="text-muted" size={20} />
                        )}
                      </button>
                      <button
                        className="btn btn-link p-0"
                        onClick={() => handleDeletePaymentMethod(method.id)}
                        title="Delete payment method"
                      >
                        <Trash2 className="text-danger" size={20} />
                      </button>
                    </div>
                  </h5>
                  <p className="card-text">
                    <strong>Card Number:</strong> •••• •••• •••• {method.card.last4}
                    <br />
                    <strong>Expiration:</strong> {method.card.exp_month.toString().padStart(2, "0")}/
                    {method.card.exp_year}
                  </p>
                  {method.default && <div className="badge bg-primary">Default</div>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <button className="btn btn-primary mt-3" onClick={() => setIsAddingCard(!isAddingCard)}>
        {isAddingCard ? "Cancel" : "Add New Card"}
      </button>
      {isAddingCard && (
        <Elements stripe={stripePromise}>
          <AddCardForm onCardAdded={handleCardAdded} />
        </Elements>
      )}
    </div>
  );
}


